import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Crown, Check, Star, Phone, RotateCw } from "lucide-react";
import { isNativeApp } from "@/lib/platform";
import { isRevenueCatEnabled, getOfferings, purchasePackage, restorePurchases, PACKAGE_PRODUCT_IDS } from "@/lib/revenuecat";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_USD = {
  "5_readings": "$2.99",
  "20_readings": "$10.99",
  "50_readings": "$19.99",
  "unlimited_monthly": "$49.99",
  "live_session": "$99.99",
};

export default function UpgradePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [rcPackages, setRcPackages] = useState(null); // { [pkgKey]: nativePackage }
  const native = isNativeApp();
  const [webPrices, setWebPrices] = useState(null);

  useEffect(() => {
    let tz = "";
    try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { /* ignore */ }
    axios.get(`${API}/payments/prices`, { params: { tz } })
      .then((res) => { if (res.data?.success) setWebPrices(res.data); })
      .catch(() => {});
  }, []);

  // Load RevenueCat offerings on native platforms.
  useEffect(() => {
    if (!isRevenueCatEnabled()) return;
    (async () => {
      const offering = await getOfferings();
      if (!offering) return;
      // Map our internal package keys to the matching native package returned by RC.
      const map = {};
      for (const pkg of offering.availablePackages || []) {
        const productId = pkg?.product?.identifier;
        for (const [key, mapped] of Object.entries(PACKAGE_PRODUCT_IDS)) {
          if (mapped === productId) map[key] = pkg;
        }
      }
      setRcPackages(map);
    })();
  }, []);

  // Apple (guideline 3.1.1) and Google Play require their own billing for
  // digital content used in the app, so reading packs and Unlimited only sell
  // through RevenueCat on phones. The live session with Mamta Dang is a
  // real-time person-to-person service (Apple 3.1.3(d)), so it keeps Stripe,
  // opened in the system browser.
  const openStripeCheckout = async (packageId) => {
    const origin = native ? process.env.REACT_APP_BACKEND_URL : window.location.origin;
    const res = await axios.post(`${API}/payments/checkout`, {
      package_id: packageId,
      origin_url: origin,
      tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone; } catch { return undefined; } })(),
    });
    if (!(res.data.success && res.data.url)) return;
    if (native) {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url: res.data.url });
    } else {
      window.location.href = res.data.url;
    }
  };

  // Show the App Store / Play price (local currency) when the store gave us one.
  // In the app, the App Store / Play price wins; otherwise the website price
  // list for the visitor's country (USD, CAD or INR).
  const webPrice = (id) => {
    const amount = webPrices?.prices?.[id];
    if (amount == null) return DEFAULT_USD[id];
    const currency = webPrices.currency || "USD";
    try {
      return new Intl.NumberFormat(currency === "INR" ? "en-IN" : "en-US", {
        style: "currency", currency, minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      }).format(amount).replace(/^CA\$/, "C$");
    } catch {
      return `${currency} ${amount}`;
    }
  };
  const storePrice = (id) => rcPackages?.[id]?.product?.priceString || webPrice(id);

  const handlePurchase = async (packageId) => {
    setLoading(packageId);
    try {
      if (packageId === "live_session" || !native) {
        await openStripeCheckout(packageId);
      } else if (isRevenueCatEnabled()) {
        const nativePkg = rcPackages?.[packageId];
        if (!nativePkg) {
          toast.error("This plan isn't available in the app yet. Please try again later.");
          return;
        }
        try {
          await purchasePackage(nativePkg);
          // RevenueCat webhook will credit the account server-side; refresh the page
          // a moment later so the new balance is visible.
          toast.success("Purchase complete — credits will appear shortly.");
          setTimeout(() => navigate("/dashboard"), 1500);
        } catch (e) {
          if (e?.userCancelled) return;
          toast.error("Purchase failed. Please try again.");
          console.error("RC purchase error:", e);
        }
      } else {
        toast.error("In-app purchases aren't set up in this build yet.");
      }
    } catch (e) {
      console.error("Checkout error:", e);
      toast.error("Couldn't start checkout. Please try again.");
    } finally {
      setLoading(null);
    }
  };

  const handleRestore = async () => {
    if (!isRevenueCatEnabled()) return;
    setLoading("restore");
    try {
      await restorePurchases();
      toast.success("Restore complete. Any active purchases will sync shortly.");
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (e) {
      toast.error("Restore failed.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 sm:py-8" data-testid="upgrade-page">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="font-cinzel text-2xl sm:text-3xl font-bold text-amber-400 tracking-wider mb-1">Reading Packages</h1>
        <p className="text-slate-500 text-xs sm:text-sm">Choose the plan that works for you</p>
      </div>

      {/* Current plan */}
      <div className="rounded-xl p-3 mb-6 border border-white/10 bg-white/[0.02] text-center">
        <p className="text-[10px] text-slate-600 uppercase tracking-wider">Current Plan</p>
        <p className="text-sm text-emerald-400 font-semibold">Free — 3 readings every 24 hours</p>
        {user?.readings_purchased > 0 && (
          <p className="text-[10px] text-amber-400 mt-0.5">+ {user.readings_purchased} purchased readings</p>
        )}
        {native && isRevenueCatEnabled() && (
          <button
            onClick={handleRestore}
            disabled={loading === "restore"}
            className="mt-2 inline-flex items-center gap-1.5 text-[10px] text-slate-400 hover:text-amber-400 transition-colors"
            data-testid="restore-purchases-btn"
          >
            <RotateCw className={`h-3 w-3 ${loading === "restore" ? "animate-spin" : ""}`} />
            Restore Purchases
          </button>
        )}
      </div>

      {/* Unlimited — Featured at top */}
      <div className="rounded-2xl p-5 mb-4 border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/5 to-amber-900/10" data-testid="pkg-unlimited_monthly">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <p className="text-[9px] text-amber-400 font-bold uppercase tracking-widest mb-1">Best Value</p>
            <div className="flex items-center gap-2 mb-1">
              <Crown className="h-5 w-5 text-amber-400" />
              <h3 className="font-cinzel text-amber-400 font-bold text-lg">Unlimited</h3>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-bold text-white">{storePrice("unlimited_monthly")}</span>
              <span className="text-xs text-slate-500">/month</span>
            </div>
          </div>
          <Button onClick={() => handlePurchase("unlimited_monthly")} disabled={loading === "unlimited_monthly"}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-6 shrink-0" data-testid="buy-unlimited_monthly">
            {loading === "unlimited_monthly" ? "..." : "Go Unlimited"}
          </Button>
        </div>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          {["Unlimited questions", "All modes (Kundli + Tarot + Compatibility)", "Priority readings", "For astrologers & power users"].map(f => (
            <span key={f} className="flex items-center gap-1 text-[11px] text-slate-300"><Check className="h-3 w-3 text-emerald-400 shrink-0" />{f}</span>
          ))}
        </div>
      </div>

      {/* Reading packs — 3 cards in a row */}
      <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-6">
        {[
          { id: "5_readings", label: "Starter", count: "5", icon: Zap },
          { id: "20_readings", label: "Popular", count: "20", icon: Sparkles, pop: true },
          { id: "50_readings", label: "Pro", count: "50", icon: Crown },
        ].map(pkg => (
          <div key={pkg.id} className={`rounded-xl p-3 sm:p-4 border text-center transition-all hover:border-amber-500/40 ${pkg.pop ? "border-amber-500/30 bg-amber-500/[0.03]" : "border-white/10 bg-white/[0.02]"}`}
            data-testid={`pkg-${pkg.id}`}>
            {pkg.pop && <p className="text-[8px] sm:text-[9px] text-amber-400 font-bold uppercase tracking-wider mb-1">Popular</p>}
            <pkg.icon className={`h-5 w-5 mx-auto mb-1 ${pkg.pop ? "text-amber-400" : "text-slate-400"}`} />
            <p className="font-bold text-white text-lg sm:text-xl">{storePrice(pkg.id)}</p>
            <p className="text-[10px] text-slate-500 mb-2">{pkg.count} readings</p>
            <Button onClick={() => handlePurchase(pkg.id)} disabled={loading === pkg.id} size="sm"
              className={`w-full text-[10px] sm:text-xs font-semibold ${pkg.pop ? "bg-amber-500 hover:bg-amber-600 text-black" : "bg-white/5 hover:bg-white/10 text-slate-300 border border-white/10"}`}
              data-testid={`buy-${pkg.id}`}>
              {loading === pkg.id ? "..." : "Buy"}
            </Button>
          </div>
        ))}
      </div>

      {/* Live Session with Mamta Dang */}
      <div className="rounded-2xl overflow-hidden border border-amber-500/20 bg-white/[0.02]" data-testid="live-session-section">
        <div className="flex flex-col sm:flex-row">
          <div className="sm:w-[200px] shrink-0">
            <img src="/mamta-dang.png" alt="Mamta Dang" className="w-full h-48 sm:h-full object-cover object-top" />
          </div>
          <div className="flex-1 p-4 sm:p-5">
            <p className="text-[9px] text-amber-400 font-bold uppercase tracking-widest mb-0.5">Premium Live Consultation</p>
            <h3 className="font-cinzel text-base sm:text-lg text-amber-400 font-bold mb-0.5">1-Hour Session with Mamta Dang</h3>
            <p className="text-[11px] text-slate-500 mb-3">Personalized Spiritual Guidance for Clarity & Direction</p>

            <div className="grid grid-cols-4 gap-1.5 mb-3">
              {[
                { label: "20+ Yrs", desc: "Experience" },
                { label: "Awards", desc: "Winning" },
                { label: "Press", desc: "Columnist" },
                { label: "5000+", desc: "Clients" },
              ].map(a => (
                <div key={a.label} className="bg-white/5 rounded-md py-1.5 px-1 text-center">
                  <p className="text-[10px] font-bold text-amber-400 leading-tight">{a.label}</p>
                  <p className="text-[8px] text-slate-600">{a.desc}</p>
                </div>
              ))}
            </div>

            <ul className="space-y-1 mb-3">
              {[
                "Kundli + Lal Kitaab + Numerology + Tarot",
                "Personalized remedies for your concerns",
                "WhatsApp follow-up included",
                "Video call or phone — worldwide",
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[11px] text-slate-300">
                  <Check className="h-3 w-3 text-emerald-400 mt-0.5 shrink-0" />{f}
                </li>
              ))}
            </ul>

            <div className="flex items-center gap-3">
              <div>
                <p className="text-xl font-bold text-white">{webPrice("live_session")}</p>
                <p className="text-[9px] text-slate-600">One-time</p>
              </div>
              <Button onClick={() => handlePurchase("live_session")} disabled={loading === "live_session"}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-bold text-xs"
                data-testid="buy-live-session">
                {loading === "live_session" ? "Processing..." : "Book Live Session"}
              </Button>
            </div>
            <p className="text-[9px] text-slate-600 mt-2">WhatsApp/Call: +1 (437) 422-3693</p>
          </div>
        </div>
      </div>

      <div className="mt-5 text-center">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-slate-600 text-xs">Back to Home</Button>
      </div>
    </div>
  );
}
